import { useState, type MouseEvent } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Tooltip,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert,
  Badge,
} from "@mui/material";
import {
  Brightness4,
  Brightness7,
  SettingsBrightness,
  Menu as MenuIcon,
  Warning,
  Loop,
  Home as HomeIcon,
} from "@mui/icons-material";
import { usePendingAttachments } from "../hooks/useAttachments";
import { useThemeMode } from "../theme/ThemeContext";
import { useNavigate } from "react-router-dom";
import { useConfig, useServerStatus } from "../api/queries";
import { type PredefinedFilterDto } from "../api/types";
import { AboutDialog } from "./AboutDialog";
import { GemmaStatusChip } from "./GemmaStatusChip";
import { buildPredefinedFilterUrl } from "../logic/predefined-filter-url";

export const Header = () => {
  const { mode, setMode } = useThemeMode();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const navigate = useNavigate();
  const { data: config } = useConfig();
  const { data: serverStatus } = useServerStatus();
  const { data: pendingAttachments } = usePendingAttachments();
  const pendingCount = pendingAttachments?.length ?? 0;

  const handleMenuOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleModeSelect = (newMode: "light" | "dark" | "system") => {
    setMode(newMode);
    handleMenuClose();
  };

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setDrawerOpen(false);
  };

  const handlePredefinedFilter = (filter: PredefinedFilterDto) => {
    navigate(buildPredefinedFilterUrl(filter));
    setDrawerOpen(false);
  };

  const getIcon = () => {
    switch (mode) {
      case "light":
        return <Brightness7 />;
      case "dark":
        return <Brightness4 />;
      case "system":
        return <SettingsBrightness />;
      default:
        return <SettingsBrightness />;
    }
  };

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: "center" }}>
      <Box
        component="img"
        src="/logo.svg"
        alt="MyMoney"
        sx={{ height: 40, my: 2 }}
      />
      <List>
        <ListItem disablePadding>
          <ListItemButton onClick={() => handleNavigation("/")}>
            <ListItemIcon>
              <Badge badgeContent={pendingCount} color="primary">
                <HomeIcon />
              </Badge>
            </ListItemIcon>
            <ListItemText primary="Home" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton onClick={() => handleNavigation("/add")}>
            <ListItemText primary="Add Transaction" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton onClick={() => handleNavigation("/transactions")}>
            <ListItemText primary="All Transactions" />
          </ListItemButton>
        </ListItem>
        {config?.predefinedFilters.map((filter) => (
          <ListItem key={filter.name} disablePadding>
            <ListItemButton
              onClick={() => handlePredefinedFilter(filter)}
              sx={{ pl: 4 }}
            >
              <ListItemText primary={filter.name} />
            </ListItemButton>
          </ListItem>
        ))}
        <ListItem disablePadding>
          <ListItemButton onClick={() => handleNavigation("/summary")}>
            <ListItemText primary="Monthly Summary" />
          </ListItemButton>
        </ListItem>
        <Divider />
        <ListItem disablePadding>
          <ListItemButton onClick={() => handleNavigation("/admin")}>
            <ListItemText primary="Administration" />
          </ListItemButton>
        </ListItem>
        <Divider />
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => {
              setAboutOpen(true);
              setDrawerOpen(false);
            }}
          >
            <ListItemText primary="About" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  const versionMismatch =
    !!serverStatus && serverStatus.version.gitCommit !== __APP_VERSION_COMMIT__;
  const isRebuilding = serverStatus?.rebuildStatus.isRebuilding;

  return (
    <AppBar position="static">
      <AboutDialog open={aboutOpen} onClose={() => setAboutOpen(false)} />

      {/* Mobile Banners */}
      {versionMismatch && (
        <Alert
          severity="warning"
          icon={<Warning fontSize="inherit" />}
          sx={{
            borderRadius: 0,
            display: { xs: "flex", md: "none" },
            alignItems: "center",
          }}
        >
          Frontend and backend versions mismatch. Please reload the page.
        </Alert>
      )}

      {isRebuilding && (
        <Alert
          severity="info"
          icon={
            <Loop
              fontSize="inherit"
              sx={{ animation: "spin 2s linear infinite" }}
            />
          }
          sx={{
            borderRadius: 0,
            display: { xs: "flex", md: "none" },
            alignItems: "center",
            "@keyframes spin": {
              "0%": { transform: "rotate(0deg)" },
              "100%": { transform: "rotate(360deg)" },
            },
          }}
        >
          Rebuilding index: {serverStatus?.rebuildStatus.progress}/
          {serverStatus?.rebuildStatus.total}
        </Alert>
      )}

      <Toolbar>
        <IconButton
          size="large"
          edge="start"
          color="inherit"
          aria-label="menu"
          sx={{ mr: 2, display: { xs: "none", sm: "flex" } }}
          onClick={handleDrawerToggle}
        >
          <MenuIcon />
        </IconButton>
        <Box
          component="img"
          src="/logo.svg"
          alt="MyMoney"
          sx={{ height: 40, mr: 2 }}
        />

        {versionMismatch && (
          <Tooltip title="Frontend and backend versions mismatch. Please reload the page.">
            <Alert
              severity="warning"
              icon={<Warning fontSize="inherit" />}
              sx={{
                mr: 2,
                py: 0,
                alignItems: "center",
                display: { xs: "none", md: "flex" },
              }}
            >
              Version Mismatch
            </Alert>
          </Tooltip>
        )}

        {isRebuilding && (
          <Tooltip
            title={`Rebuilding index: ${serverStatus?.rebuildStatus.progress}/${serverStatus?.rebuildStatus.total}`}
          >
            <Alert
              severity="info"
              icon={
                <Loop
                  fontSize="inherit"
                  sx={{ animation: "spin 2s linear infinite" }}
                />
              }
              sx={{
                mr: 2,
                py: 0,
                alignItems: "center",
                display: { xs: "none", md: "flex" },
                "@keyframes spin": {
                  "0%": {
                    transform: "rotate(0deg)",
                  },
                  "100%": {
                    transform: "rotate(360deg)",
                  },
                },
              }}
            >
              Rebuilding...
            </Alert>
          </Tooltip>
        )}

        <Box sx={{ flexGrow: 1 }} />
        <GemmaStatusChip />
        <Box sx={{ display: { xs: "none", sm: "block" } }}>
          <Tooltip title="Switch theme">
            <IconButton
              size="large"
              aria-label="theme switcher"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenuOpen}
              color="inherit"
            >
              {getIcon()}
            </IconButton>
          </Tooltip>
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            keepMounted
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem
              selected={mode === "light"}
              onClick={() => handleModeSelect("light")}
            >
              Light
            </MenuItem>
            <MenuItem
              selected={mode === "dark"}
              onClick={() => handleModeSelect("dark")}
            >
              Dark
            </MenuItem>
            <MenuItem
              selected={mode === "system"}
              onClick={() => handleModeSelect("system")}
            >
              System
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
      <Drawer
        variant="temporary"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          "& .MuiDrawer-paper": { boxSizing: "border-box", width: 240 },
        }}
      >
        {drawer}
      </Drawer>
    </AppBar>
  );
};
